import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { roles, hasPermissions } from 'src/utils/roles';

import { useGetLlmConfig } from 'src/actions/system/llm-config';
import {
  useChatWidgetStore,
  CHAT_WIDGET_MIN_WIDTH,
  getChatWidgetMaxWidth,
} from 'src/stores/chat-widget';

import { Iconify } from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';

import { useAuthContext } from 'src/auth/hooks';

import { ChatMessageList } from './chat-message-list';
import { useChatStore } from '../hooks/use-chat-store';
import { ChatMessageInput } from './chat-message-input';
import { ReportInsightsIcon } from './report-insights-icon';
import { ChatTokenStatusBar } from './chat-token-status-bar';
import { useKnowledgeChat } from '../hooks/use-knowledge-chat';
import { ConversationTypeIcon } from './conversation-type-icon';
import { useMessageFeedback } from '../hooks/use-message-feedback';
import { ReportContextStatusOverlay } from './report-context-status-overlay';

// ----------------------------------------------------------------------

const DEFAULT_SUBJECT = 'New Conversation';

/** @param {{ open?: boolean, onClose?: () => void, onOpenFullView?: () => void }} props */
export function ChatWidget(props) {
  const { user } = useAuthContext();

  // Gate on permission in a wrapper so the hooks below always run in the same
  // order for a given mounted instance — bailing out mid-function (after some
  // hooks already ran) breaks React's rules of hooks when `user` changes
  // (e.g. switching accounts) causes this check to flip between renders.
  if (!user?.features?.chat || !hasPermissions(user, [roles.AI])) return null;

  return <ChatWidgetPanel {...props} user={user} />;
}

/** @param {{ open?: boolean, onClose?: () => void, onOpenFullView?: () => void, user: any }} props */
function ChatWidgetPanel({ open, onClose, onOpenFullView, user }) {
  const router = useRouter();
  const theme = useTheme();

  const canSeeConversationType = hasPermissions(user, [roles.AI_GLOBAL]);
  const reportViewActive = useChatWidgetStore((state) => state.reportViewActive);
  const forceGlobal = useChatWidgetStore((state) => state.forceGlobal);
  const openReport = useChatWidgetStore((state) => state.openReport);
  const isReportInsights = reportViewActive && !forceGlobal;

  const width = useChatWidgetStore((state) => state.width);
  const setWidth = useChatWidgetStore((state) => state.setWidth);
  const isResizing = useChatWidgetStore((state) => state.isResizing);
  const setIsResizing = useChatWidgetStore((state) => state.setIsResizing);

  const handleResizeStart = useCallback(
    /** @param {React.MouseEvent} e */
    (e) => {
      e.preventDefault();
      setIsResizing(true);
    },
    [setIsResizing]
  );

  const handleResizeKeyDown = useCallback(
    /** @param {React.KeyboardEvent} e */
    (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setWidth(width + 16);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setWidth(width - 16);
      }
    },
    [setWidth, width]
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    /** @param {MouseEvent} e */
    const handleMouseMove = (e) => setWidth(window.innerWidth - e.clientX);
    const handleMouseUp = () => setIsResizing(false);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setWidth, setIsResizing]);

  const {
    conversations,
    conversationsLoaded,
    activeConversation,
    activeConversationId,
    setActiveConversation,
    createConversation,
    appendMessages,
    updateMessageById,
    updateSubject,
    updateUsage,
    upsertConversationFromServer,
    deleteConversation,
  } = useChatStore({ conversationType: isReportInsights ? 'report' : undefined });

  // A conversation keeps the type it was opened with (a new one is stamped with the current
  // mode); with none selected, the header follows the current page mode.
  const showReportHeader = activeConversation?.conversationType
    ? activeConversation.conversationType === 'report'
    : isReportInsights;

  const setActiveConversationType = useChatWidgetStore((state) => state.setActiveConversationType);
  useEffect(() => {
    setActiveConversationType(showReportHeader ? 'report' : 'global');
  }, [showReportHeader, setActiveConversationType]);

  const [showHistory, setShowHistory] = useState(false);

  // The header's Insights icon asks for a fresh global chat when a report conversation is showing.
  const newChatRequestId = useChatWidgetStore((state) => state.newChatRequestId);
  const lastNewChatRequestIdRef = useRef(newChatRequestId);
  useEffect(() => {
    if (newChatRequestId === lastNewChatRequestIdRef.current) return;
    lastNewChatRequestIdRef.current = newChatRequestId;
    createConversation(DEFAULT_SUBJECT);
    setShowHistory(false);
  }, [newChatRequestId, createConversation]);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const deleteConfirm = useBoolean();

  const settings = useSettingsContext();
  const { data: llmConfig } = useGetLlmConfig();
  const availableModels = llmConfig?.availableBedrockModels || [];
  const selectedModelArn = settings.defaultAIModel || '';

  const handleModelChange = useCallback((newModelArn) => {
    settings.onUpdateField('defaultAIModel', newModelArn);
  }, [settings]);

  useEffect(() => {
    if (availableModels.length > 0) {
      const isModelAvailable = availableModels.some((m) => m.value === selectedModelArn);

      if (!isModelAvailable) {
        const defaultModel = availableModels[0].value;
        settings.onUpdateField('defaultAIModel', defaultModel);
      }
    }
  }, [availableModels, selectedModelArn, settings]);

  const {
    sendMessage,
    sending,
    loadingMessageId,
    cancelMessage,
    exportingReportContext,
    savingReportContext,
  } = useKnowledgeChat({
    activeConversation,
    activeConversationId,
    appendMessages,
    updateMessageById,
    updateSubject,
    updateUsage,
    upsertConversationFromServer,
    modelArn: selectedModelArn,
    forceGlobalChat: forceGlobal,
  });

  const getMessageById = useCallback(
    (messageId) => activeConversation?.messages?.find((msg) => msg.id === messageId) ?? null,
    [activeConversation]
  );

  const { submitFeedback } = useMessageFeedback({ updateMessageById, getMessageById });

  const handleFeedback = useCallback(
    (messageId, feedback) => submitFeedback(messageId, feedback),
    [submitFeedback]
  );

  const [pendingSend, setPendingSend] = useState(
    /** @type {{ text: string, files?: File[], conversationId: string } | null} */ (null)
  );
  const activeConversationIdRef = useRef(activeConversationId);
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const handleSend = useCallback(
    /** @param {string} text @param {File[]} [files] */
    (text, files) => {
      if (sending || exportingReportContext) return;
      if (activeConversationIdRef.current) {
        sendMessage(text, files);
        return;
      }
      const conv = createConversation(DEFAULT_SUBJECT);
      setPendingSend({ text, files, conversationId: conv.id });
    },
    [createConversation, sendMessage, sending, exportingReportContext]
  );

  useEffect(() => {
    if (!pendingSend) return;
    if (activeConversationId !== pendingSend.conversationId) return;
    sendMessage(pendingSend.text, pendingSend.files);
    setPendingSend(null);
  }, [activeConversationId, pendingSend, sendMessage]);

  const handleOpenFull = () => {
    onClose?.();
    router.push(activeConversationId ? `${paths.chat}?id=${activeConversationId}` : paths.chat);
  };

  if (!open || !user?.features?.chat || !hasPermissions(user, [roles.AI])) return null;

  return (
    <>
    <ReportContextStatusOverlay exporting={exportingReportContext} saving={savingReportContext} />
    <Box
      sx={{
        position: 'fixed',
        // Below `md`: full-viewport takeover (no room to sit beside content).
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Above the header/nav, but below MUI drawers/modals (e.g. the account/sign-out drawer).
        zIndex: (t) => t.zIndex.drawer - 1,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: (t) => t.shadows[20],
        bgcolor: 'background.paper',
        overflow: 'hidden',
        [theme.breakpoints.up('md')]: {
          // From `md` up: an edge-anchored, user-resizable sidebar below the header, sized so
          // it never overlaps the app's main content.
          left: 'auto',
          top: 'var(--layout-header-mobile-height)',
          height: 'calc(100dvh - var(--layout-header-mobile-height))',
          width: `${width}px`,
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
        },
        [theme.breakpoints.up('lg')]: {
          top: 'var(--layout-header-desktop-height)',
          height: 'calc(100dvh - var(--layout-header-desktop-height))',
        },
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat sidebar"
        aria-valuenow={Math.round(width)}
        aria-valuemin={CHAT_WIDGET_MIN_WIDTH}
        aria-valuemax={Math.round(getChatWidgetMaxWidth())}
        tabIndex={0}
        onMouseDown={handleResizeStart}
        onKeyDown={handleResizeKeyDown}
        sx={{
          display: 'none',
          [theme.breakpoints.up('md')]: {
            display: 'block',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1,
            '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
            ...(isResizing && { bgcolor: 'primary.main', opacity: 0.4 }),
          },
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.neutral',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {showReportHeader ? (
            <ReportInsightsIcon size={24} />
          ) : (
            <Iconify icon="solar:stars-outline" width={24} />
          )}
          <Typography variant="subtitle1">{showReportHeader ? 'Report Insights' : 'Global Insights'}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          <IconButton
            size="small"
            onClick={() => {
              setShowHistory(false);
              if (showReportHeader && !isReportInsights && reportViewActive) {
                openReport();
                return;
              }
              createConversation(DEFAULT_SUBJECT);
            }}
            title="New chat"
          >
            <Iconify icon="solar:add-circle-bold" width={20} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setShowHistory((prev) => !prev)}
            title="History"
            sx={{
              ...(showHistory && {
                bgcolor: 'action.selected',
              }),
            }}
          >
            <Iconify icon="solar:history-bold" width={20} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => window.open(paths.helpInsights, '_blank', 'noopener')}
            title="AI Insights Help"
          >
            <Iconify icon="solar:question-circle-bold" width={20} />
          </IconButton>
          <IconButton size="small" onClick={handleOpenFull} title="Open full chat">
            <Iconify icon="solar:maximize-square-bold" width={20} />
          </IconButton>
          <IconButton size="small" onClick={onClose} title="Close">
            <Iconify icon="solar:close-circle-bold" width={20} />
          </IconButton>
        </Stack>
      </Stack>

      {showHistory ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!conversationsLoaded ? (
            <LinearProgress sx={{ mx: 2, my: 3 }} />
          ) : (
            <List disablePadding>
              {conversations.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No past conversations
                  </Typography>
                </Box>
              )}

            {conversations.map((conv) => {
              const isSelected = activeConversationId === conv.id;
              return (
                <ListItem key={conv.id} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => {
                      setActiveConversation(conv.id);
                      setShowHistory(false);
                    }}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    {canSeeConversationType ? (
                      <ConversationTypeIcon
                        conversationType={conv.conversationType}
                        size={24}
                        iconWidth={14}
                        sx={{ mr: 1.5 }}
                      />
                    ) : (
                      <Iconify
                        icon="solar:chat-round-line-bold"
                        width={20}
                        sx={{ mr: 1.5, color: isSelected ? 'primary.main' : 'text.secondary' }}
                      />
                    )}
                    <ListItemText
                      primary={conv.subject || 'Untitled'}
                      primaryTypographyProps={{
                        noWrap: true,
                        variant: 'body2',
                        color: isSelected ? 'primary.main' : 'text.primary',
                      }}
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTargetId(conv.id);
                        deleteConfirm.onTrue();
                      }}
                      sx={{
                        ml: 1,
                        color: 'text.disabled',
                        '&:hover': { color: 'error.main' },
                      }}
                      aria-label="Delete conversation"
                    >
                      <Iconify icon="solar:trash-bin-2-bold" width={18} />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          )}
        </Box>
      ) : (
        <>
          <ChatMessageList
            messages={activeConversation?.messages || []}
            currentUserId="me"
            loadingMessageId={loadingMessageId}
            variant="chatgpt"
            onSuggestionClick={handleSend}
            onFeedback={handleFeedback}
            compactTables
          />

          <Box
            sx={{
              p: 1.5,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
            }}
          >
            {sending && (
              <LinearProgress
                sx={{ borderRadius: 1, mb: 1, height: 2 }}
              />
            )}
            <ChatMessageInput
              onSend={handleSend}
              onStop={cancelMessage}
              sending={sending}
              disabled={false}
              blocked={exportingReportContext}
              sendingLabel={savingReportContext ? 'Saving report data...' : undefined}
              variant="chatgpt"
            />
            <ChatTokenStatusBar
              selectedModelArn={selectedModelArn}
              availableModels={availableModels}
              onModelChange={handleModelChange}
              messageCount={activeConversation?.messages?.length ?? 0}
              promptTokens={activeConversation?.usage?.promptTokens ?? 0}
              completionTokens={activeConversation?.usage?.completionTokens ?? 0}
              totalTokens={activeConversation?.usage?.totalTokens ?? 0}
              compact
            />
          </Box>
        </>
      )}

      <ConfirmDialog
        open={deleteConfirm.value}
        onClose={() => {
          deleteConfirm.onFalse();
          setDeleteTargetId(null);
        }}
        title="Delete conversation"
        content="This conversation and its messages will be permanently removed."
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteTargetId) {
                deleteConversation(deleteTargetId);
              }
              setDeleteTargetId(null);
              deleteConfirm.onFalse();
            }}
          >
            Delete
          </Button>
        }
      />
    </Box>
    </>
  );
}

// ----------------------------------------------------------------------

export function ChatWidgetFab({ open, onToggle }) {
  const { user } = useAuthContext();

  if (!user?.features?.chat || !hasPermissions(user, [roles.AI])) return null;

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onToggle}
      startIcon={<ReportInsightsIcon size={20} />}
      sx={{
        position: 'fixed',
        bottom: 72,
        right: 24,
        zIndex: (theme) => theme.zIndex.drawer - 2,
        height: 44,
        px: 2.5,
        borderRadius: 999,
        typography: 'subtitle2',
        boxShadow: (theme) => theme.shadows[12],
        '&:hover': { bgcolor: 'primary.dark', boxShadow: (theme) => theme.shadows[16] },
        ...(open && { display: 'none' }),
      }}
      title="Open Report Insights"
    >
      Report Insights
    </Button>
  );
}
